<template>
  <div class="nc-stepper" data-testid="nc-stepper">
    <!-- Step indicator -->
    <div class="nc-stepper__head">
      <button
        v-for="(step, i) in normalizedSteps"
        :key="i"
        type="button"
        class="nc-stepper__step"
        :data-testid="`nc-stepper-step-${i}`"
        :class="{
          'nc-stepper__step--active': i === modelValue,
          'nc-stepper__step--done': i < modelValue,
          'nc-stepper__step--clickable': canJumpTo(i),
        }"
        :disabled="!canJumpTo(i)"
        @click="jumpTo(i)"
      >
        <span class="nc-stepper__dot">
          <mdicon v-if="i < modelValue" name="check" :size="14" />
          <template v-else>{{ i + 1 }}</template>
        </span>
        <span class="nc-stepper__label">{{ step.label }}</span>
        <span v-if="i < normalizedSteps.length - 1" class="nc-stepper__line" />
      </button>
    </div>

    <!-- Step body -->
    <div class="nc-stepper__body">
      <slot :step="modelValue" :is-last="isLast" />
    </div>

    <!-- Navigation -->
    <div v-if="!hideNav" class="nc-stepper__nav">
      <NcButton
        fill="clear"
        color="medium"
        data-testid="nc-stepper-prev"
        :disabled="modelValue === 0 || loading"
        @click="prev"
      >
        <mdicon name="chevron-left" :size="18" />
        Vorige
      </NcButton>
      <span v-if="!(isLast && hasFinishSlot)" class="nc-stepper__progress">
        Stap {{ modelValue + 1 }} / {{ normalizedSteps.length }}
      </span>
      <NcButton
        v-if="!isLast"
        color="primary"
        data-testid="nc-stepper-next"
        :disabled="!canAdvance || loading"
        @click="next"
      >
        Volgende
        <mdicon name="chevron-right" :size="18" />
      </NcButton>
      <!-- Custom final action(s), e.g. a dual draft/complete save. -->
      <slot v-else-if="hasFinishSlot" name="finish" :can-advance="canAdvance" />
      <NcButton
        v-else
        color="primary"
        data-testid="nc-stepper-finish"
        :disabled="!canAdvance"
        :loading="loading"
        @click="finish"
      >
        {{ finishLabel }}
      </NcButton>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NcStepper',
  props: {
    modelValue: { type: Number, default: 0 },
    // Array of strings or { label } objects.
    steps: { type: Array, required: true },
    // Gate progression / finishing per step (parent-driven validation).
    canAdvance: { type: Boolean, default: true },
    // Allow tapping the header to jump to already-visited steps.
    linear: { type: Boolean, default: true },
    finishLabel: { type: String, default: 'Opslaan' },
    loading: { type: Boolean, default: false },
    hideNav: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'finish'],
  computed: {
    normalizedSteps() {
      return this.steps.map((s) => (typeof s === 'string' ? { label: s } : s));
    },
    isLast() {
      return this.modelValue >= this.normalizedSteps.length - 1;
    },
    hasFinishSlot() {
      return !!this.$slots.finish;
    },
  },
  methods: {
    canJumpTo(i) {
      if (i === this.modelValue) return false;
      // In linear mode only earlier (already-visited) steps are tappable.
      return this.linear ? i < this.modelValue : true;
    },
    jumpTo(i) {
      if (this.loading) return;
      if (this.canJumpTo(i)) this.$emit('update:modelValue', i);
    },
    prev() {
      if (this.modelValue > 0) this.$emit('update:modelValue', this.modelValue - 1);
    },
    next() {
      if (!this.canAdvance) return;
      if (!this.isLast) this.$emit('update:modelValue', this.modelValue + 1);
    },
    finish() {
      if (this.canAdvance) this.$emit('finish');
    },
  },
};
</script>

<style scoped>
.nc-stepper {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Indicator */
.nc-stepper__head {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px 4px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.nc-stepper__step {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  min-width: 64px;
  gap: 6px;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font-family: inherit;
  color: inherit;
  -webkit-appearance: none;
  appearance: none;
}
.nc-stepper__step--clickable {
  cursor: pointer;
}
.nc-stepper__dot {
  position: relative;
  z-index: 1;
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  background: var(--nc-bg-page, #f0f0f2);
  color: var(--nc-text-secondary, #92949c);
  border: 2px solid var(--nc-border, #d0d5dd);
  transition: all 0.15s;
}
.nc-stepper__label {
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  line-height: 1.2;
  color: var(--nc-text-secondary, #92949c);
}
.nc-stepper__line {
  position: absolute;
  top: 13px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: var(--nc-border, #d0d5dd);
  z-index: 0;
}
.nc-stepper__step--active .nc-stepper__dot {
  background: var(--nc-color-primary, #14b69a);
  border-color: var(--nc-color-primary, #14b69a);
  color: #fff;
}
.nc-stepper__step--active .nc-stepper__label {
  color: var(--nc-text, #1a1a2e);
  font-weight: 600;
}
.nc-stepper__step--done .nc-stepper__dot {
  background: var(--nc-color-primary, #14b69a);
  border-color: var(--nc-color-primary, #14b69a);
  color: #fff;
}
.nc-stepper__step--done .nc-stepper__line {
  background: var(--nc-color-primary, #14b69a);
}

/* Body */
.nc-stepper__body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
}

/* Navigation */
.nc-stepper__nav {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px;
  background: var(--nc-bg-card, #fff);
  border-top: 1px solid var(--nc-border, #e5e7eb);
}
.nc-stepper__progress {
  font-size: 12px;
  color: var(--nc-text-secondary, #92949c);
  white-space: nowrap;
}
</style>
